import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiFetch, apiDownload, urlPoza } from "./api";
import { statusExpirare, textStare, formatData } from "../utils/expirare";
import { ETICHETE, stareMasina } from "../utils/flota";

// v9.0:
// • Secțiune nouă „Acte mașină" — Talonul și CIV-ul (cartea mașinii), încărcate
//   de echipa LEASEIN, pot fi deschise direct de client.
// • Dacă mașina e „în service", apare un banner + pozele cu update-uri din
//   service (cu notele echipei).
// • CASCO nu mai apare — clientul vede doar RCA / ITP / Rovinietă.

const ETICHETE_ACTE = { talon: "Talon (certificat de înmatriculare)", civ: "CIV (cartea mașinii)" };

export default function MasinaDetaliu() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [masina, setMasina] = useState(null);
  const [stare, setStare] = useState("loading"); // loading | ok | error | notfound
  const [acte, setActe] = useState([]);
  const [pozeService, setPozeService] = useState([]);
  const [deschidere, setDeschidere] = useState(null); // id-ul actului care se deschide

  useEffect(() => {
    apiFetch("/masini")
      .then((data) => {
        const gasit = (data.masini || []).find((m) => String(m.id) === String(id));
        if (gasit) {
          setMasina(gasit);
          setStare("ok");
        } else {
          setStare("notfound");
        }
      })
      .catch(() => setStare("error"));
    apiFetch(`/masini/${id}/documente`)
      .then((d) => {
        if (d && d.ok) {
          setActe(d.documente || []);
          setPozeService(d.pozeService || []);
        }
      })
      .catch(() => {});
  }, [id]);

  // Deschide un act (Talon/CIV) sau o poză într-un tab nou.
  // Nu putem folosi un simplu <a href> fiindcă API-ul cere header-ul Authorization.
  async function deschideDocument(docId) {
    setDeschidere(docId);
    try {
      const blob = await apiDownload(`/documente/${docId}/file`);
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message || "Nu am putut deschide documentul.");
    } finally {
      setDeschidere(null);
    }
  }

  if (stare === "loading") {
    return (
      <div className="detaliu">
        <Link to="/portal/masini" className="detaliu__inapoi">← Înapoi la mașini</Link>
        <div className="detaliu__card detaliu__card--skel">
          <span className="skel skel--line" style={{ width: 180, height: 30 }} />
          <span className="skel skel--line" style={{ width: 240, height: 16, marginTop: 12 }} />
        </div>
      </div>
    );
  }

  if (stare === "error" || stare === "notfound") {
    return (
      <div className="detaliu">
        <Link to="/portal/masini" className="detaliu__inapoi">← Înapoi la mașini</Link>
        <div className="portal__loading">
          {stare === "notfound"
            ? "Mașina nu a fost găsită în flota ta."
            : "Nu am putut încărca datele mașinii. Reîncearcă mai târziu."}
        </div>
      </div>
    );
  }

  const sm = stareMasina(masina);
  const inService = masina.statusOperational === "in_service";
  // Doar documentele pe care clientul trebuie să le vadă (RCA/ITP/Rovinietă).
  const documente = Object.keys(ETICHETE).map((tip) => [tip, (masina.expira || {})[tip]]);

  return (
    <div className="detaliu">
      <Link to="/portal/masini" className="detaliu__inapoi">← Înapoi la mașini</Link>

      <header className={`detaliu__hero detaliu__hero--${sm.stare}`}>
        {urlPoza(masina.poza) && (
          <figure className="detaliu__foto">
            <img src={urlPoza(masina.poza)} alt={masina.model || "Mașină"} loading="lazy"
                 onError={(e) => { e.currentTarget.closest("figure").style.display = "none"; }} />
            {masina.pozaAtribuire && (
              <figcaption className="detaliu__foto-atrib">{masina.pozaAtribuire}</figcaption>
            )}
          </figure>
        )}
        <div>
          <span className="detaliu__eyebrow">Mașină din flotă</span>
          <h1>{masina.nrInmatriculare || "—"}</h1>
          <p className="detaliu__model">{masina.model || "Model nespecificat"}</p>
          {inService && <span className="pill pill--service">🔧 Momentan în service</span>}
        </div>
        <StareGenerala stare={sm.stare} zile={sm.zile} />
      </header>

      {inService && (
        <div className="service-banner">
          <strong>Această mașină este momentan în service.</strong>
          <span>
            {pozeService.length > 0
              ? " Mai jos găsești pozele cu stadiul lucrărilor, actualizate de echipa LEASEIN."
              : " Echipa LEASEIN va publica aici update-uri pe măsură ce avansează lucrările."}
          </span>
        </div>
      )}

      <section className="detaliu__sectiune">
        <h2>Documente</h2>
        <div className="detaliu__docs">
          {documente.map(([tip, data]) => {
            const s = statusExpirare(data);
            return (
              <div className={`docrow docrow--${s.stare}`} key={tip}>
                <div className="docrow__left">
                  <span className={`dot dot--${s.stare}`} />
                  <span className="docrow__nume">{ETICHETE[tip]}</span>
                </div>
                <span className="docrow__data">{formatData(data)}</span>
                <span className={`docrow__stare docrow__stare--${s.stare}`}>
                  {textStare(s.zile)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="detaliu__sectiune">
        <h2>Acte mașină</h2>
        {acte.length === 0 ? (
          <div className="dash__empty">
            Talonul și cartea mașinii nu au fost încărcate încă. Le poți cere printr-o solicitare.
          </div>
        ) : (
          <div className="acte__grid">
            {acte.map((a) => (
              <button
                key={a.id}
                className="acte__card"
                onClick={() => deschideDocument(a.id)}
                disabled={deschidere === a.id}
              >
                <span className="acte__icon">{(a.mime || "").startsWith("image/") ? "🖼" : "📄"}</span>
                <span className="acte__nume">{ETICHETE_ACTE[a.tip] || a.fisier || "Document"}</span>
                <span className="acte__actiune">
                  {deschidere === a.id ? "Se deschide…" : "Vezi documentul →"}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {pozeService.length > 0 && (
        <section className="detaliu__sectiune">
          <h2>Update-uri din service</h2>
          <div className="sfoto__grid">
            {pozeService.map((p) => (
              <PozaService key={p.id} poza={p} onOpen={() => deschideDocument(p.id)} />
            ))}
          </div>
        </section>
      )}

      <section className="detaliu__sectiune">
        <h2>Detalii vehicul</h2>
        <dl className="detaliu__specs">
          <div className="spec">
            <dt>Număr înmatriculare</dt>
            <dd>{masina.nrInmatriculare || "—"}</dd>
          </div>
          <div className="spec">
            <dt>Marcă / Model</dt>
            <dd>{masina.model || "—"}</dd>
          </div>
          <div className="spec">
            <dt>Serie șasiu</dt>
            <dd>{masina.serieSasiu || "—"}</dd>
          </div>
          <div className="spec">
            <dt>Status</dt>
            <dd>{inService ? "🔧 În service" : "✅ Funcțională"}</dd>
          </div>
        </dl>
      </section>

      <div className="dash__cta">
        <div>
          <h3>Ai nevoie de ceva pentru această mașină?</h3>
          <p>Trimite o solicitare și revenim cât mai repede.</p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() =>
            navigate("/portal/solicitari", {
              state: { masina: masina.nrInmatriculare },
            })
          }
        >
          Solicitare nouă
        </button>
      </div>
    </div>
  );
}

// Poza din service: încărcată autentificat (Bearer), afișată din blob.
function PozaService({ poza, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let activ = true;
    let url = null;
    apiDownload(`/documente/${poza.id}/file`)
      .then((blob) => {
        if (!activ) return;
        url = window.URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      activ = false;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [poza.id]);

  return (
    <figure className="sfoto">
      {src ? (
        <img src={src} alt={poza.nota || "Update din service"} onClick={onOpen}
             style={{ cursor: "zoom-in" }} />
      ) : (
        <span className="sfoto__placeholder skel" />
      )}
      <figcaption>
        {poza.nota && <span className="sfoto__nota">{poza.nota}</span>}
        <span className="sfoto__data">{formatData(poza.creatLa)}</span>
      </figcaption>
    </figure>
  );
}

function StareGenerala({ stare, zile }) {
  const map = {
    rosu: { text: "Necesită acțiune", sub: "Are documente expirate" },
    galben: { text: "Atenție", sub: textStare(zile) },
    verde: { text: "În regulă", sub: "Toate documentele valabile" },
    necunoscut: { text: "Date incomplete", sub: "Lipsesc unele date" },
  };
  const info = map[stare] || map.necunoscut;
  return (
    <div className={`detaliu__stare detaliu__stare--${stare}`}>
      <span className={`dot dot--${stare}`} />
      <div>
        <strong>{info.text}</strong>
        <small>{info.sub}</small>
      </div>
    </div>
  );
}
